import { Signal } from '../utilities/async/Signal';
import { resolveItems, supportFacets } from './getItems';
import { waitForNextStoreUpdate } from '../store/enhancers/NextStoreUpdateEnhancer';
import { logPageLoad } from './logPageLoad';
import { now, mark } from '../utilities/performance';
import { Facet } from '../models/Facet';
import { KeyFacet } from '../models/KeyFacet';
import { IItemCacheThunkAction, IItemCacheDispatch, IItemCacheContext } from '../store/actions/ItemCacheActions';
import { IOnCurrentItemKeyChangedProps } from '../components/itemsNavigator/ItemsNavigator';
import { registerPermanentItemDemands } from './pruneItems';
import { readItems } from './readItems';
import { IItemCache } from '../models/IItemCacheObserver';
import {
    pageLoadConfigurationFacet,
    hasPageLoadedFacet,
    lastNavigationEventFacet,
    itemsScopeInitTimeFacet,
    resetPerfLoggerKeyFacet,
    resetPerfLoggerFacet
} from '../facets/LoggerFacets';
import { configurationItemKey } from '../facets/ConfigurationFacets';
import { createAddon } from '../addons/registerOperationHandler';

const pageLoadKeyFacet = new KeyFacet<{}>('pageLoad', {});

const pageLoadSignalFacet = new Facet<Signal<void>>('pageLoadSignal');

const pageLoadAddon = createAddon(() => {
    return (dispatch: IItemCacheDispatch) => {
        dispatch(supportFacets({
            keyFacet: pageLoadKeyFacet,
            facets: []
        }));
    };
});

function getIsPageLoadBarrierEnabled(): IItemCacheThunkAction<boolean> {
    return (dispatch: IItemCacheDispatch, getState: () => {}, context: IItemCacheContext) => {
        const pageLoadConfiguration = pageLoadConfigurationFacet.unpack(context.configuration);

        const {
            enablePageLoadBarrier = false
        } = pageLoadConfiguration || {};

        return enablePageLoadBarrier;
    };
}

/**
 * @internal
 */
export function isPageLoaded(itemCache: IItemCache): boolean {
    const pageLoadKey = pageLoadKeyFacet.serialize({});

    const pageLoadConfiguration = itemCache.demandItemFacet(pageLoadConfigurationFacet, configurationItemKey);

    const {
        enablePageLoadBarrier = false
    } = pageLoadConfiguration || {};

    return !enablePageLoadBarrier || !!itemCache.demandItemFacet(hasPageLoadedFacet, pageLoadKey);
}

/**
 * @public
 */
export function getHasPageLoaded(): IItemCacheThunkAction<boolean> {
    return (dispatch: IItemCacheDispatch) => {
        dispatch(pageLoadAddon);

        return dispatch(readItems(isPageLoaded));
    };
}

/**
 * @internal
 */
export function signalItemsViewRenderComplete(itemsScopeItemKey: string): IItemCacheThunkAction<void> {
    return (dispatch: IItemCacheDispatch) => {
        dispatch(pageLoadAddon);

        const pageLoadKey = pageLoadKeyFacet.serialize({});

        const {
            pageLoadSignal,
            hasPageLoaded,
            itemsViewRenderCompleteInfo
        } = dispatch(readItems((itemCache: IItemCache) => {
            return {
                pageLoadSignal: itemCache.demandItemFacet(pageLoadSignalFacet, pageLoadKey),
                hasPageLoaded: itemCache.demandItemFacet(hasPageLoadedFacet, pageLoadKey),
                itemsViewRenderCompleteInfo: itemCache.demandItemFacet(lastNavigationEventFacet, itemsScopeItemKey)
            };
        }));

        const {
            renderComplete = false,
            keyChanges = undefined
        } = itemsViewRenderCompleteInfo || {};

        // Collect timestamp if we haven't report render complete yet
        let timestamp = !renderComplete ? now() : undefined;
        const {
            ScopeInitTime = NaN
        } = dispatch(readItems((itemCache: IItemCache) => {
            return {
                ScopeInitTime: itemCache.demandItemFacet(itemsScopeInitTimeFacet, itemsScopeItemKey)
            };
        }));

        if (timestamp) {
            dispatch(logPageLoad(timestamp, {
                isFullPageLoad: !hasPageLoaded,
                keyChanges: keyChanges,
                itemsScopeItemKey: itemsScopeItemKey,
                itemsScopeInitTime: ScopeInitTime
            }));
        }

        let signal: Signal<void>;

        if (pageLoadSignal) {
            signal = pageLoadSignal;
        } else {
            signal = new Signal<void>();
        }

        dispatch(resolveItems({
            items: {
                [itemsScopeItemKey]: {
                    ...lastNavigationEventFacet.pack({
                        renderComplete: true,
                        keyChanges: keyChanges
                    })
                },
                [pageLoadKey]: {
                    ...pageLoadSignalFacet.pack(signal),
                    ...hasPageLoadedFacet.pack(true)
                }
            }
        }));

        dispatch(registerPermanentItemDemands({
            items: {
                [pageLoadKey]: {}
            }
        }));

        dispatch(waitForNextStoreUpdate()).then(() => signal.resolve());
    };
}

/**
 * @internal
 */
export function signalItemsViewNavigationStart(payload: {
    itemsScopeItemKey: string,
} & IOnCurrentItemKeyChangedProps): IItemCacheThunkAction<void> {
    const {
        itemsScopeItemKey,
        ...itemKeyChange
    } = payload;

    return (dispatch: IItemCacheDispatch) => {
        dispatch(pageLoadAddon);

        mark(`signalItemsViweNavigationStart_key(${itemsScopeItemKey})`);

        const pageLoadKey = pageLoadKeyFacet.serialize({});
        const resetPerfLoggerKey = resetPerfLoggerKeyFacet.serialize({});

        const {
            hasPageLoaded,
            resetPerfLogger
        } = dispatch(readItems((itemCache: IItemCache) => {
            return {
                hasPageLoaded: itemCache.demandItemFacet(hasPageLoadedFacet, pageLoadKey),
                resetPerfLogger: itemCache.demandItemFacet(resetPerfLoggerFacet, resetPerfLoggerKey)
            };
        }));

        if (resetPerfLogger) {
            resetPerfLogger({
                isFullPageLoad: !hasPageLoaded,
                keyChanges: itemKeyChange,
                itemsScopeItemKey: itemsScopeItemKey
            });
        }

        dispatch(resolveItems({
            items: {
                [itemsScopeItemKey]: {
                    ...lastNavigationEventFacet.pack({
                        renderComplete: false,
                        keyChanges: itemKeyChange
                    })
                }
            }
        }));
    };
}

/**
 * @public
 */
export function waitForPageLoad(): IItemCacheThunkAction<Promise<void>> {
    return async (dispatch: IItemCacheDispatch) => {
        dispatch(pageLoadAddon);

        const enablePageLoadBarrier = dispatch(getIsPageLoadBarrierEnabled());

        if (!enablePageLoadBarrier) {
            return;
        }

        const pageLoadKey = pageLoadKeyFacet.serialize({});

        const pageLoadSignal = dispatch(readItems((itemCache: IItemCache) => itemCache.demandItemFacet(pageLoadSignalFacet, pageLoadKey)));

        let signal: Signal<void>;

        if (pageLoadSignal) {
            signal = pageLoadSignal;
        } else {
            signal = new Signal<void>();

            dispatch(resolveItems({
                items: {
                    [pageLoadKey]: {
                        ...pageLoadSignalFacet.pack(signal)
                    }
                }
            }));

            registerPermanentItemDemands({
                items: {
                    [pageLoadKey]: {}
                }
            });
        }

        await signal.promise;
    };
}
